<script setup>
/**
 * 「个人中心」侧滑抽屉。
 *
 * 旧版这块不是各游戏页各写一份，而是工具栏第三个按钮 <ion-menu-button> 触发的
 * 全局唯一 <ion-menu>（见 web/29.42d9f906f231ff156120.js 模板工厂节点 31）。
 * 因此这里做成单一共享组件，动画参数直接取自 Ionic 的 overlay 菜单实现
 * （web/95.a62b7eb6d6dcfef156e7.js）：
 *
 *   baseAnimation: easing "cubic-bezier(0.0, 0.0, 0.2, 1)"
 *                  easingReverse "cubic-bezier(0.4, 0.0, 0.6, 1)"
 *                  duration 300
 *   overlay:       MENU_CONTENT_OPEN = 8
 *                  menuInnerEl  translateX  -(width + 8)px -> 0px
 *                  backdropEl   opacity     0.01 -> 0.32
 *   尺寸:          --width:304px，@media (max-width:340px) 时 264px
 *   阴影(md):      0 2px 22px 0 rgba(0,0,0,.09), 4px 0 16px 0 rgba(0,0,0,.18)
 *
 * backdrop 与 menu-inner 在 Ionic 里是兄弟节点、各自独立动画，这里保持同样结构，
 * 否则遮罩的 opacity 会连带把抽屉内容一起变透明。
 */
defineProps({
  open: { type: Boolean, default: false },
  userProfile: Object,
  availableBalance: Number,
})
const emit = defineEmits(['close'])
const money = (value) => Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
// 图标改回旧版那套彩色 iconfont（旧版 this.pages 里的 "#icon-xxx" symbol，
// 来自 web/assets/fonts/iconfont/iconfont.js，已逐个导出成
// public/legacy/svg/iconfont/*.svg，多色 fill 原样保留）。
// 之前用的 /legacy/svg/ios-*.svg 是 Ionicons 单色线性图标，跟旧版不一致。
const items = [
  ['/user/service', '/legacy/svg/iconfont/icon-kefu.svg', '联系客服'],
  ['/user/password', '/legacy/svg/iconfont/icon-password.svg', '修改密码'],
  ['/user/bank', '/legacy/svg/iconfont/icon-bank-card.svg', '收款账号'],
  ['/user/finance', '/legacy/svg/iconfont/icon-money-log.svg', '交易明细'],
  ['/user/recharge', '/legacy/svg/iconfont/icon-recharge.svg', '充值记录'],
  ['/user/cash', '/legacy/svg/iconfont/icon-cash.svg', '提现记录'],
  // 旧版 /game（chunk 25 的 app-game）是个只有顶栏「游戏中心」的空壳页，
  // 实际是直达 /tabs/home，所以这里指向 /home
  ['/home', '/legacy/svg/iconfont/icon-game-home.svg', '游戏大厅'],
  ['/user/bets', '/legacy/svg/iconfont/icon-bet-log.svg', '游戏记录'],
  // 「游戏账单」原来错指到了游戏记录页；旧版它是 chunk 23 的 app-bill，路由 /game/bill
  ['/game/bill', '/legacy/svg/iconfont/icon-game-bill.svg', '游戏账单'],
  ['/logout', '/legacy/svg/iconfont/icon-logout.svg', '安全退出'],
]
</script>

<template>
  <Transition name="pc-drawer">
    <div v-if="open" class="pc-menu" role="dialog" aria-modal="true" aria-label="个人中心">
      <div class="pc-backdrop" @click="emit('close')"></div>
      <aside class="pc-inner">
        <header>
          <button type="button" aria-label="关闭" @click="emit('close')">‹</button>
          <b>个人中心</b>
          <RouterLink to="/user/profile" @click="emit('close')">♟</RouterLink>
        </header>
        <section class="identity">
          <img src="/legacy/images/userface-default.png" alt="">
          <div>
            <strong>{{ userProfile?.nickname || userProfile?.username }}</strong>
            <small>账号：{{ userProfile?.username }}</small>
          </div>
        </section>
        <section class="total"><span>账户余额：</span><b>￥{{ money(availableBalance) }}</b></section>
        <section class="balance"><span>可用：{{ money(availableBalance) }}</span><span>冻结：{{ money(userProfile?.frozen) }}</span></section>
        <section class="cash">
          <RouterLink to="/user/money/1" @click="emit('close')">充值</RouterLink>
          <RouterLink to="/user/money/2" @click="emit('close')">提现</RouterLink>
        </section>
        <nav>
          <RouterLink v-for="item in items" :key="item[2]" :to="item[0]" @click="emit('close')">
            <img :src="item[1]" alt="">{{ item[2] }}<i>›</i>
          </RouterLink>
        </nav>
      </aside>
    </div>
  </Transition>
</template>

<style scoped>
.pc-menu,.pc-menu *{box-sizing:border-box;font-family:Arial,"Microsoft YaHei",sans-serif;font-style:normal;font-weight:400;line-height:normal}
.pc-menu{position:fixed;inset:0;z-index:1100;font-size:13px}
/* 对应 ion-backdrop：纯黑底，透明度由动画从 .01 推到 .32 */
.pc-backdrop{position:absolute;inset:0;background:#000;opacity:.32}
/* 对应 .menu-inner：绝对定位在左侧、满高、固定宽度、md overlay 阴影 */
.pc-inner{position:absolute;top:0;bottom:0;left:0;display:flex;width:304px;flex-direction:column;overflow-y:auto;background:#f4f4f4;box-shadow:0 2px 22px 0 rgb(0 0 0 / 9%),4px 0 16px 0 rgb(0 0 0 / 18%)}
@media (max-width:340px){.pc-inner{width:264px}}

/* Ionic overlay 动画：抽屉 translateX -(304+8)px -> 0，遮罩 opacity .01 -> .32，
   两者都是 300ms；进入用 easing，离开用 easingReverse。 */
.pc-drawer-enter-active .pc-backdrop,.pc-drawer-leave-active .pc-backdrop,
.pc-drawer-enter-active .pc-inner,.pc-drawer-leave-active .pc-inner{transition-duration:300ms;transition-property:opacity,transform}
.pc-drawer-enter-active .pc-backdrop,.pc-drawer-enter-active .pc-inner{transition-timing-function:cubic-bezier(0,0,.2,1)}
.pc-drawer-leave-active .pc-backdrop,.pc-drawer-leave-active .pc-inner{transition-timing-function:cubic-bezier(.4,0,.6,1)}
.pc-drawer-enter-from .pc-backdrop,.pc-drawer-leave-to .pc-backdrop{opacity:.01}
.pc-drawer-enter-from .pc-inner,.pc-drawer-leave-to .pc-inner{transform:translateX(-312px)}
@media (max-width:340px){.pc-drawer-enter-from .pc-inner,.pc-drawer-leave-to .pc-inner{transform:translateX(-272px)}}

header{display:flex;height:52px;align-items:center;justify-content:space-between;padding:0 15px;background:#f04141;color:#fff}
header b{font-size:18px;font-weight:500;line-height:52px}
header button,header a{border:0;background:transparent;color:#fff;font-size:28px;text-decoration:none}
.identity{display:flex;align-items:center;gap:12px;padding:16px 13px;background:#fff}
.identity img{width:56px;height:56px;border-radius:50%}
.identity div{display:grid;gap:5px}
.identity strong{font-size:17px;font-weight:700}
.identity small{color:#aaa;font-size:13px}
.total,.balance,.cash{display:grid;background:#fff;text-align:center}
.total{border-top:8px solid #f4f4f4;padding:14px;font-size:13px}
.total b{font-size:15px;font-weight:700}
.balance,.cash{grid-template-columns:1fr 1fr;border-top:1px solid #ddd;font-size:13px}
.balance span,.cash a{border-right:1px solid #ddd;padding:12px;color:#333;text-decoration:none}
.balance span:last-child,.cash a:last-child{border-right:0}
nav{margin-top:8px}
nav a{display:flex;height:47px;align-items:center;gap:10px;border-bottom:1px solid #ddd;padding:0 13px;background:#fff;color:#333;font-size:13px;text-decoration:none}
nav a:nth-child(3),nav a:nth-child(7),nav a:nth-child(10){margin-top:7px}
/* 旧版是 <svg class="icon"><use> 直出 symbol，没有额外降透明度，
   彩色图标不能再套 opacity，否则颜色发灰 */
nav img{width:19px;height:19px}
nav i{margin-left:auto;color:#bbb;font-size:24px;font-style:normal}
</style>
